import './game.css';
import '../party/party.css';
import { relatedToolsMarkup, siteBrandMarkup } from '../site-chrome';
import { kakaoCardShareMessage, preloadKakaoShare } from '../kakao-share';
import {
  DRAWING_RELAY_MAX_POINTS_PER_STROKE,
  DRAWING_RELAY_MAX_STROKES,
  isDrawingRelayView,
  type DrawingRelayArtifact,
  type DrawingRelayColor,
  type DrawingRelayStageView,
  type DrawingRelayStroke,
  type DrawingRelayView,
  type DrawingRelayWidth,
} from './core';
import { PartyNetwork, type PartyConnectionState } from '../party/network';
import {
  normalizePartyRoomCode,
  type PartyRoomClosedReason,
  type PartyRoomView,
  type PartyServerMessage,
} from '../party/protocol';
import {
  PARTY_PLAYER_COLORS,
  copyPartyText,
  escapePartyHtml,
  inviteRoomFromLocation,
  partyInviteUrl,
  partyPlayersMarkup,
  savedPartyNickname,
  savePartyNickname,
  sharePartyInvite,
} from '../party/ui';

const GAME_PATH = '/games/wavebean-drawing-relay/';
const GAME_TITLE = '파도콩 그림릴레이';
const DRAWING_ASPECT_RATIO = 16 / 10;

const COLOR_VALUES: Readonly<Record<DrawingRelayColor, string>> = {
  ink: '#172338',
  coral: '#ff746d',
  sun: '#f6c842',
  aqua: '#39c9bd',
  eraser: '#ffffff',
};

function requireElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`${selector} 요소를 찾지 못했습니다.`);
  return element;
}

function closedReasonCopy(reason: PartyRoomClosedReason): string {
  if (reason === 'left') return '방에서 나왔습니다.';
  if (reason === 'room-expired') return '오랫동안 사용하지 않아 방이 종료됐습니다.';
  return '서버 점검으로 방이 종료됐습니다. 잠시 후 다시 시도해 주세요.';
}

function connectionCopy(state: PartyConnectionState): string {
  if (state === 'connected') return '서버 연결됨';
  if (state === 'connecting') return '연결하는 중';
  if (state === 'reconnecting') return '재접속하는 중';
  return '연결 대기';
}

function taskCopy(task: DrawingRelayStageView['task']): {
  readonly eyebrow: string;
  readonly title: string;
  readonly description: string;
} {
  if (task === 'prompt') {
    return {
      eyebrow: '첫 번째 이야기',
      title: '친구가 그릴 제시어를 적어주세요',
      description: '상황이 구체적일수록 마지막 반전이 더 재미있어요.',
    };
  }
  if (task === 'draw') {
    return {
      eyebrow: '그림으로 전달하기',
      title: '글자 없이 그림으로만 표현해요',
      description: '잘 그리는 것보다 알아볼 듯 말 듯 그리는 게 포인트예요.',
    };
  }
  return {
    eyebrow: '그림을 보고 맞히기',
    title: '이 그림은 무엇일까요?',
    description: '원래 제시어는 보이지 않아요. 보이는 대로 솔직하게 적어주세요.',
  };
}

function drawStrokes(
  context: CanvasRenderingContext2D,
  strokes: readonly DrawingRelayStroke[],
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  context.save();
  context.beginPath();
  context.rect(x, y, width, height);
  context.clip();
  for (const stroke of strokes) {
    if (stroke.points.length === 0) continue;
    context.beginPath();
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = COLOR_VALUES[stroke.color];
    context.lineWidth = (stroke.width === 'thick' ? 0.022 : 0.009) * Math.min(width, height);
    const first = stroke.points[0];
    context.moveTo(x + (first.x / 10_000) * width, y + (first.y / 10_000) * height);
    for (const point of stroke.points.slice(1)) {
      context.lineTo(x + (point.x / 10_000) * width, y + (point.y / 10_000) * height);
    }
    context.stroke();
  }
  context.restore();
}

function makeStrokeId(playerId: string | null): string {
  const random = crypto.getRandomValues(new Uint32Array(1))[0].toString(36);
  return `${playerId?.slice(0, 8) ?? 'guest'}-${Date.now().toString(36)}-${random}`.slice(0, 48);
}

export function mountDrawingRelay(): void {
  preloadKakaoShare();
  const app = requireElement<HTMLDivElement>('#app');
  document.documentElement.classList.add('party-page', 'drawing-relay-page');
  document.title = `${GAME_TITLE} | 오늘툴`;

  app.innerHTML = `
    <div class="party-shell relay-shell">
      <header class="site-brand-header party-header">
        ${siteBrandMarkup()}
        <span class="party-header-badge">3–8인 · 온라인 친구 방</span>
      </header>

      <main>
        <section class="party-intro">
          <span>DRAW · GUESS · REVEAL</span>
          <h1>파도콩 그림릴레이</h1>
          <p>제시어를 그림으로, 그림을 다시 답으로 이어 보세요.<br />마지막에 완성된 엉뚱한 이야기를 친구들과 함께 공개해요.</p>
        </section>

        <section class="party-card" aria-label="파도콩 그림릴레이 게임">
          <div class="party-card-bar">
            <span id="party-connection" class="party-connection offline">연결 대기</span>
            <div id="party-room-mini" class="party-room-mini" hidden>
              <span>방</span><strong id="party-room-mini-code"></strong>
              <button type="button" data-party-action="copy-code">코드 복사</button>
            </div>
          </div>
          <div id="party-surface" class="party-surface" aria-live="polite"></div>
        </section>

        <section class="party-help" aria-label="게임 방법">
          <article><strong>1. 동시에 시작</strong><p>모두 제시어를 하나씩 쓰고, 받은 내용만 보고 그림과 답을 번갈아 만들어요.</p></article>
          <article><strong>2. 비밀로 전달</strong><p>내 차례에 필요한 한 장만 보여요. 친구들의 결과는 마지막까지 비밀이에요.</p></article>
          <article><strong>3. 함께 공개</strong><p>방장이 한 이야기씩 열면 처음 제시어가 어떻게 변했는지 모두 함께 확인해요.</p></article>
        </section>

        ${relatedToolsMarkup('wavebean-drawing-relay')}
      </main>

      <footer class="party-footer">
        <a href="/privacy/">개인정보처리방침</a>
        <a href="/terms/">이용약관</a>
        <span>그림과 제시어는 이 방이 끝나면 서버에 남지 않아요.</span>
      </footer>
    </div>`;

  const surface = requireElement<HTMLDivElement>('#party-surface');
  const connectionBadge = requireElement<HTMLSpanElement>('#party-connection');
  const roomMini = requireElement<HTMLDivElement>('#party-room-mini');
  const roomMiniCode = requireElement<HTMLElement>('#party-room-mini-code');
  const incomingRoom = inviteRoomFromLocation();

  let connection: PartyConnectionState = 'offline';
  let room: PartyRoomView | null = null;
  let notice = '';
  let currentSurfaceKey = '';
  let serverClockDelta = 0;
  let selectedColor: DrawingRelayColor = 'ink';
  let selectedWidth: DrawingRelayWidth = 'thin';
  let localStrokes: DrawingRelayStroke[] = [];
  let activeStroke: DrawingRelayStroke | null = null;
  let serverDraftRevision = -1;
  let artifactCounter = 0;
  let artifactCanvases = new Map<string, DrawingRelayArtifact>();

  const network = new PartyNetwork('drawing-relay', {
    onConnection(nextConnection) {
      connection = nextConnection;
      updateConnection();
      updateReconnectOverlay();
      if (!room) renderSurface(true);
    },
    onMessage(message) {
      handleMessage(message);
    },
  });

  function ownPlayer() {
    return room?.players.find((player) => player.id === network.playerId) ?? null;
  }

  function gameView(): DrawingRelayView | null {
    return room && isDrawingRelayView(room.game) ? room.game : null;
  }

  function participantForId(playerId: string) {
    const currentView = gameView();
    const historical = currentView && (currentView.mode === 'reveal' || currentView.mode === 'complete')
      ? currentView.participants.find((player) => player.id === playerId)
      : null;
    return room?.players.find((player) => player.id === playerId) ?? historical ?? null;
  }

  function updateConnection(): void {
    connectionBadge.textContent = connectionCopy(connection);
    connectionBadge.className = `party-connection ${connection === 'connected' ? '' : connection === 'offline' ? 'offline' : 'connecting'}`.trim();
  }

  function updateRoomMini(): void {
    roomMini.hidden = !room;
    roomMiniCode.textContent = room?.roomCode ?? '';
  }

  function updateUrl(roomCode: string | null): void {
    const url = new URL(window.location.href);
    if (roomCode) url.searchParams.set('room', roomCode);
    else url.searchParams.delete('room');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }

  function surfaceKey(): string {
    if (!room) return `landing:${incomingRoom ?? ''}:${notice}:${connection}`;
    if (room.phase === 'lobby') {
      const players = room.players.map((player) => `${player.id}:${player.ready}:${player.connected}:${player.isHost}`).join('|');
      return `lobby:${room.hostId}:${players}:${notice}`;
    }
    const view = gameView();
    if (!view) return `invalid:${room.phase}:${notice}`;
    if (view.mode === 'stage') {
      const submitted = view.submittedPlayerIds.includes(network.playerId ?? '');
      return `stage:${view.matchId}:${view.phaseId}:${submitted}:${notice}`;
    }
    if (view.mode === 'reveal') return `reveal:${view.matchId}:${view.phaseId}:${room.hostId}:${notice}`;
    return `complete:${view.matchId}:${room.rematchReadyPlayerIds.join(',')}:${notice}`;
  }

  function renderSurface(force = false): void {
    const nextKey = surfaceKey();
    if (!force && nextKey === currentSurfaceKey) {
      updateLiveStage();
      updateRoomMini();
      updateReconnectOverlay();
      return;
    }
    currentSurfaceKey = nextKey;
    artifactCounter = 0;
    artifactCanvases = new Map();
    if (!room) renderLanding();
    else if (room.phase === 'lobby') renderLobby();
    else {
      const view = gameView();
      if (!view) renderInvalidState();
      else if (view.mode === 'stage') renderStage(view);
      else if (view.mode === 'reveal') renderReveal(view);
      else renderComplete(view);
    }
    updateRoomMini();
    hydrateArtifactCanvases();
    updateReconnectOverlay();
  }

  function renderLanding(): void {
    const nickname = savedPartyNickname();
    const joinCode = incomingRoom ?? '';
    const busy = connection === 'connecting' || connection === 'reconnecting';
    surface.innerHTML = `
      <section class="party-panel center relay-entry-panel">
        <span class="party-eyebrow">친구들과 바로 시작</span>
        <h2>${joinCode ? '초대받은 방에 들어갈까요?' : '새 이야기를 만들어 볼까요?'}</h2>
        <p>가입 없이 닉네임만 정하면 돼요. 3명부터 시작할 수 있어요.</p>
        <form id="relay-entry-form">
          <label class="party-field">
            <span>내 닉네임 · 최대 10자</span>
            <input id="relay-nickname" name="nickname" maxlength="10" autocomplete="nickname" value="${escapePartyHtml(nickname)}" placeholder="예: 파도콩" required />
          </label>
          <button class="party-primary full" type="submit" data-entry-mode="create" ${busy ? 'disabled' : ''}>새 방 만들기</button>
          <div class="party-divider">또는 방 코드로 참가</div>
          <div class="party-join-row">
            <label class="party-field relay-code-field">
              <span>6자리 방 코드</span>
              <input id="relay-room-code" name="roomCode" maxlength="6" autocapitalize="characters" spellcheck="false" value="${escapePartyHtml(joinCode)}" placeholder="ABC234" />
            </label>
            <button class="party-primary blue" type="submit" data-entry-mode="join" ${busy ? 'disabled' : ''}>참가하기</button>
          </div>
        </form>
        <p class="party-notice" role="status">${escapePartyHtml(notice)}</p>
      </section>`;
  }

  function renderLobby(): void {
    if (!room) return;
    const self = ownPlayer();
    const isHost = self?.isHost ?? false;
    const connectedReady = room.players.every((player) => player.connected && player.ready);
    const canStart = room.players.length >= room.minPlayers && connectedReady;
    const missing = Math.max(0, room.minPlayers - room.players.length);
    surface.innerHTML = `
      <section class="party-panel center relay-lobby-panel">
        <span class="party-eyebrow">그림릴레이 대기방 · ${room.players.length}/${room.maxPlayers}</span>
        <h2>${missing > 0 ? `${missing}명만 더 오면 시작!` : '친구들이 준비하고 있어요'}</h2>
        <p>아래 링크나 방 코드를 보내세요. 참가자는 준비 버튼을 눌러주세요.</p>
        <div class="party-room-code" aria-label="방 코드 ${room.roomCode}">${room.roomCode}</div>
        <div class="party-invite-actions">
          <button class="party-quiet-button" type="button" data-party-action="copy-invite">초대 링크 복사</button>
          <button class="party-quiet-button" type="button" data-party-action="share-invite">카카오톡으로 초대</button>
        </div>
        ${partyPlayersMarkup(room, network.playerId)}
        ${isHost ? `
          <button class="party-primary full coral" type="button" data-party-action="start" ${canStart ? '' : 'disabled'}>
            ${canStart ? '그림릴레이 시작!' : room.players.length < room.minPlayers ? `최소 ${room.minPlayers}명이 필요해요` : '모두 준비하면 시작할 수 있어요'}
          </button>` : `
          <button class="party-primary full ${self?.ready ? 'sun' : ''}" type="button" data-party-action="ready">
            ${self?.ready ? '준비 취소' : '준비 완료!'}
          </button>`}
        <button class="relay-leave-button" type="button" data-party-action="leave">방 나가기</button>
        <p class="party-notice" role="status">${escapePartyHtml(notice)}</p>
      </section>`;
  }

  function progressMarkup(view: DrawingRelayStageView): string {
    return `
      <div class="party-progress-row">
        <strong>${view.stageIndex + 1}/${view.stageCount} 단계 · ${view.submittedPlayerIds.length}/${view.stageCount}명 완료</strong>
        <span class="party-timer" data-relay-timer>--초</span>
      </div>`;
  }

  function waitingMarkup(view: DrawingRelayStageView): string {
    return `
      ${progressMarkup(view)}
      <section class="party-waiting relay-stage-waiting">
        <div class="party-waiting-beans" aria-hidden="true">
          ${[0, 1, 2].map((index) => `<span class="party-bean-avatar" style="--party-player-color:${PARTY_PLAYER_COLORS[index]}"><i></i></span>`).join('')}
        </div>
        <h2>답을 다음 친구에게 전달했어요</h2>
        <p>다른 파도콩들이 완성할 때까지 잠시만 기다려주세요.</p>
        <strong class="relay-submit-count" data-relay-submit-count>${view.submittedPlayerIds.length}/${view.stageCount}명 완료</strong>
      </section>`;
  }

  function renderStage(view: DrawingRelayStageView): void {
    const submitted = view.submittedPlayerIds.includes(network.playerId ?? '');
    if (submitted) {
      localStrokes = [];
      activeStroke = null;
      serverDraftRevision = -1;
      surface.innerHTML = waitingMarkup(view);
      updateTimer();
      return;
    }
    const copy = taskCopy(view.task);
    if (view.task === 'prompt') {
      surface.innerHTML = `
        ${progressMarkup(view)}
        <section class="relay-task-panel relay-text-task">
          <span class="party-eyebrow">${copy.eyebrow}</span>
          <h2>${copy.title}</h2>
          <p>${copy.description}</p>
          <form id="relay-text-form" data-task="prompt">
            <label class="party-field">
              <span>제시어 · 최대 40자</span>
              <input id="relay-task-text" maxlength="40" autocomplete="off" placeholder="예: 우산을 쓴 문어가 라면 먹기" required autofocus />
            </label>
            <button class="party-primary full coral" type="submit">이 제시어 전달하기</button>
          </form>
          <p class="party-notice" role="status">${escapePartyHtml(notice)}</p>
        </section>`;
    } else if (view.task === 'guess') {
      surface.innerHTML = `
        ${progressMarkup(view)}
        <section class="relay-task-panel relay-guess-task">
          <span class="party-eyebrow">${copy.eyebrow}</span>
          <h2>${copy.title}</h2>
          <p>${copy.description}</p>
          ${view.previous ? artifactMarkup(view.previous, 'relay-guess-artifact') : ''}
          <form id="relay-text-form" data-task="guess">
            <label class="party-field">
              <span>내가 본 답 · 최대 40자</span>
              <input id="relay-task-text" maxlength="40" autocomplete="off" placeholder="그림에서 보이는 것을 적어주세요" required />
            </label>
            <button class="party-primary full blue" type="submit">이 답 전달하기</button>
          </form>
          <p class="party-notice" role="status">${escapePartyHtml(notice)}</p>
        </section>`;
    } else {
      localStrokes = view.draft?.strokes.map((stroke) => ({ ...stroke, points: [...stroke.points] })) ?? [];
      serverDraftRevision = view.draft?.revision ?? 0;
      activeStroke = null;
      const prompt = view.previous?.kind === 'text' ? view.previous.text : '받은 제시어';
      surface.innerHTML = `
        ${progressMarkup(view)}
        <section class="relay-task-panel relay-draw-task">
          <span class="party-eyebrow">${copy.eyebrow}</span>
          <div class="relay-prompt-card"><small>내가 그릴 내용</small><strong>${escapePartyHtml(prompt)}</strong></div>
          <div class="relay-sketchbook">
            <span class="relay-tape tape-left" aria-hidden="true"></span>
            <span class="relay-tape tape-right" aria-hidden="true"></span>
            <canvas id="relay-drawing-canvas" width="720" height="450" aria-label="그림 그리기 영역"></canvas>
          </div>
          <div class="relay-tools" aria-label="그림 도구">
            <div class="relay-color-tools">
              ${(['ink', 'coral', 'sun', 'aqua'] as const).map((color) => `<button class="relay-tool-color ${selectedColor === color ? 'selected' : ''}" type="button" data-color="${color}" style="--relay-tool-color:${COLOR_VALUES[color]}" aria-label="${color} 색상"></button>`).join('')}
              <button class="relay-eraser ${selectedColor === 'eraser' ? 'selected' : ''}" type="button" data-color="eraser" aria-label="지우개">지우개</button>
            </div>
            <div class="relay-width-tools">
              <button class="relay-width ${selectedWidth === 'thin' ? 'selected' : ''}" type="button" data-width="thin">얇게</button>
              <button class="relay-width ${selectedWidth === 'thick' ? 'selected' : ''}" type="button" data-width="thick">굵게</button>
            </div>
            <div class="relay-edit-tools">
              <button type="button" data-party-action="undo" ${localStrokes.length === 0 ? 'disabled' : ''}>↶ 실행 취소</button>
              <button type="button" data-party-action="clear" ${localStrokes.length === 0 ? 'disabled' : ''}>전체 지우기</button>
            </div>
          </div>
          <button class="party-primary full coral" type="button" data-party-action="submit-drawing">그림 전달하기</button>
          <p class="party-notice" role="status">${escapePartyHtml(notice)}</p>
        </section>`;
      bindDrawingCanvas(view);
    }
    hydrateArtifactCanvases();
    updateTimer();
  }

  function authorMarkup(authorId: string): string {
    const player = participantForId(authorId);
    const color = PARTY_PLAYER_COLORS[(player?.colorIndex ?? 0) % PARTY_PLAYER_COLORS.length];
    return `<span class="relay-author"><span class="party-bean-avatar" style="--party-player-color:${color}" aria-hidden="true"><i></i></span>${escapePartyHtml(player?.nickname ?? '파도콩')}</span>`;
  }

  function artifactMarkup(artifact: DrawingRelayArtifact, className = ''): string {
    const key = `artifact-${artifactCounter}`;
    artifactCounter += 1;
    artifactCanvases.set(key, artifact);
    const timedOut = artifact.timedOut ? '<span class="relay-timeout">시간 초과</span>' : '';
    if (artifact.kind === 'text') {
      return `
        <article class="relay-artifact relay-artifact-text ${className}">
          <div class="relay-artifact-meta">${authorMarkup(artifact.authorId)}${timedOut}</div>
          <strong>${escapePartyHtml(artifact.text)}</strong>
        </article>`;
    }
    return `
      <article class="relay-artifact relay-artifact-drawing ${className}">
        <div class="relay-artifact-meta">${authorMarkup(artifact.authorId)}${timedOut}</div>
        <canvas data-relay-artifact="${key}" width="640" height="400" aria-label="${escapePartyHtml(participantForId(artifact.authorId)?.nickname ?? '참가자')}의 그림"></canvas>
      </article>`;
  }

  function chainMarkup(entries: readonly DrawingRelayArtifact[]): string {
    return `<div class="relay-chain">${entries.map((entry, index) => `${index > 0 ? '<span class="relay-chain-arrow" aria-hidden="true">↓</span>' : ''}${artifactMarkup(entry)}`).join('')}</div>`;
  }

  function renderReveal(view: Extract<DrawingRelayView, { mode: 'reveal' }>): void {
    const isHost = room?.hostId === network.playerId;
    surface.innerHTML = `
      <section class="relay-reveal-panel">
        <div class="relay-reveal-heading">
          <div><span class="party-eyebrow">결과 공개 · ${view.chainIndex + 1}/${view.chainCount}</span><h2>이 이야기는 이렇게 변했어요!</h2></div>
          <span class="relay-story-number">STORY ${view.chainIndex + 1}</span>
        </div>
        ${chainMarkup(view.entries)}
        ${isHost
    ? `<button class="party-primary full coral" type="button" data-party-action="next-reveal">${view.chainIndex + 1 >= view.chainCount ? '전체 결과 보기' : '다음 이야기 공개'} →</button>`
    : '<div class="relay-host-wait">방장이 다음 이야기를 열고 있어요…</div>'}
        <p class="party-notice" role="status">${escapePartyHtml(notice)}</p>
      </section>`;
  }

  function renderComplete(view: Extract<DrawingRelayView, { mode: 'complete' }>): void {
    const rematchReady = room?.rematchReadyPlayerIds.includes(network.playerId ?? '') ?? false;
    surface.innerHTML = `
      <section class="relay-complete-panel">
        <span class="party-eyebrow">모든 이야기 공개 완료</span>
        <h2>오늘의 그림릴레이 완성!</h2>
        <p>마음에 드는 반전은 저장해 두고, 친구들과 한 판 더 이어가세요.</p>
        <div class="relay-result-actions">
          <button class="party-primary sun" type="button" data-party-action="save-result">PNG 저장</button>
          <button class="party-primary blue" type="button" data-party-action="share-result">결과 공유</button>
        </div>
        <div class="relay-all-chains">
          ${view.chains.map((entries, index) => `<section><h3>STORY ${index + 1}</h3>${chainMarkup(entries)}</section>`).join('')}
        </div>
        <div class="relay-rematch-box">
          <strong>${room?.rematchReadyPlayerIds.length ?? 0}/${room?.players.filter((player) => player.connected).length ?? 0}명 재대결 준비</strong>
          <p>연결된 친구들이 모두 누르면 바로 새 게임이 시작돼요.</p>
          <button class="party-primary full coral" type="button" data-party-action="rematch" ${rematchReady ? 'disabled' : ''}>${rematchReady ? '친구들을 기다리는 중' : '한 판 더!'}</button>
        </div>
        <button class="relay-leave-button" type="button" data-party-action="leave">방 나가기</button>
        <p class="party-notice" role="status">${escapePartyHtml(notice)}</p>
      </section>`;
  }

  function renderInvalidState(): void {
    surface.innerHTML = `
      <section class="party-panel center">
        <span class="party-eyebrow">새로고침이 필요해요</span>
        <h2>게임 상태를 읽지 못했습니다</h2>
        <p>페이지를 새로고침해 같은 방으로 다시 연결해 주세요.</p>
        <button class="party-primary" type="button" data-party-action="reload">새로고침</button>
      </section>`;
  }

  function updateLiveStage(): void {
    const view = gameView();
    if (!view || view.mode !== 'stage') return;
    document.querySelectorAll<HTMLElement>('[data-relay-submit-count]').forEach((element) => {
      element.textContent = `${view.submittedPlayerIds.length}/${view.stageCount}명 완료`;
    });
    const canvas = document.querySelector<HTMLCanvasElement>('#relay-drawing-canvas');
    if (canvas && view.draft && view.draft.revision !== serverDraftRevision && !activeStroke) {
      serverDraftRevision = view.draft.revision;
      localStrokes = view.draft.strokes.map((stroke) => ({ ...stroke, points: [...stroke.points] }));
      paintEditorCanvas(canvas);
      updateDrawingButtons();
    }
    updateTimer();
  }

  function updateTimer(): void {
    const view = gameView();
    if (!view || view.mode !== 'stage') return;
    const remaining = Math.max(0, Math.ceil((view.deadlineAt - (Date.now() + serverClockDelta)) / 1_000));
    document.querySelectorAll<HTMLElement>('[data-relay-timer]').forEach((element) => {
      element.textContent = `${remaining}초`;
      element.classList.toggle('danger', remaining <= 10);
    });
  }

  function updateReconnectOverlay(): void {
    surface.querySelector('.relay-reconnect-overlay')?.remove();
    if (!room || connection === 'connected') return;
    const overlay = document.createElement('div');
    overlay.className = 'relay-reconnect-overlay';
    overlay.innerHTML = `<strong>${connection === 'reconnecting' ? '재접속하는 중…' : '연결이 끊겼어요'}</strong><span>현재 화면을 유지하고 서버 연결을 복구하고 있어요.</span>`;
    surface.append(overlay);
  }

  function paintEditorCanvas(canvas: HTMLCanvasElement): void {
    const context = canvas.getContext('2d');
    if (!context) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    const cssWidth = Math.max(1, rect.width || 720);
    const cssHeight = Math.max(1, rect.height || cssWidth / DRAWING_ASPECT_RATIO);
    const requiredWidth = Math.round(cssWidth * dpr);
    const requiredHeight = Math.round(cssHeight * dpr);
    if (canvas.width !== requiredWidth || canvas.height !== requiredHeight) {
      canvas.width = requiredWidth;
      canvas.height = requiredHeight;
    }
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, cssWidth, cssHeight);
    drawStrokes(context, [...localStrokes, ...(activeStroke ? [activeStroke] : [])], 0, 0, cssWidth, cssHeight);
  }

  function normalizedPoint(event: PointerEvent, canvas: HTMLCanvasElement): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(10_000, Math.round(((event.clientX - rect.left) / rect.width) * 10_000))),
      y: Math.max(0, Math.min(10_000, Math.round(((event.clientY - rect.top) / rect.height) * 10_000))),
    };
  }

  function bindDrawingCanvas(view: DrawingRelayStageView): void {
    const canvas = document.querySelector<HTMLCanvasElement>('#relay-drawing-canvas');
    if (!canvas) return;
    paintEditorCanvas(canvas);

    canvas.addEventListener('pointerdown', (event) => {
      if (connection !== 'connected' || localStrokes.length >= DRAWING_RELAY_MAX_STROKES) return;
      event.preventDefault();
      canvas.setPointerCapture(event.pointerId);
      const point = normalizedPoint(event, canvas);
      activeStroke = {
        id: makeStrokeId(network.playerId),
        color: selectedColor,
        width: selectedWidth,
        points: [point],
      };
      paintEditorCanvas(canvas);
    });

    canvas.addEventListener('pointermove', (event) => {
      if (!activeStroke || !canvas.hasPointerCapture(event.pointerId)) return;
      event.preventDefault();
      const point = normalizedPoint(event, canvas);
      const previous = activeStroke.points[activeStroke.points.length - 1];
      const dx = point.x - previous.x;
      const dy = point.y - previous.y;
      if (dx * dx + dy * dy < 45 * 45) return;
      if (activeStroke.points.length >= DRAWING_RELAY_MAX_POINTS_PER_STROKE) return;
      activeStroke = { ...activeStroke, points: [...activeStroke.points, point] };
      paintEditorCanvas(canvas);
    });

    const finishStroke = (event: PointerEvent) => {
      if (!activeStroke) return;
      event.preventDefault();
      if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
      let stroke = activeStroke;
      activeStroke = null;
      if (stroke.points.length === 1) {
        const point = stroke.points[0];
        stroke = { ...stroke, points: [point, { x: Math.min(10_000, point.x + 1), y: point.y }] };
      }
      localStrokes = [...localStrokes, stroke];
      paintEditorCanvas(canvas);
      updateDrawingButtons();
      network.action({ type: 'stroke', phaseId: view.phaseId, stroke });
    };
    canvas.addEventListener('pointerup', finishStroke);
    canvas.addEventListener('pointercancel', finishStroke);
  }

  function updateDrawingButtons(): void {
    surface.querySelectorAll<HTMLButtonElement>('[data-party-action="undo"], [data-party-action="clear"]').forEach((button) => {
      button.disabled = localStrokes.length === 0;
    });
  }

  function hydrateArtifactCanvases(): void {
    surface.querySelectorAll<HTMLCanvasElement>('[data-relay-artifact]').forEach((canvas) => {
      const artifact = artifactCanvases.get(canvas.dataset.relayArtifact ?? '');
      if (!artifact || artifact.kind !== 'drawing') return;
      const context = canvas.getContext('2d');
      if (!context) return;
      context.fillStyle = '#fff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      drawStrokes(context, artifact.strokes, 0, 0, canvas.width, canvas.height);
    });
  }

  function handleRoomUpdate(nextRoom: PartyRoomView, reconnected = false): void {
    if (reconnected) {
      activeStroke = null;
      localStrokes = [];
      serverDraftRevision = -1;
    }
    room = nextRoom;
    serverClockDelta = nextRoom.serverTime - Date.now();
    notice = '';
    updateUrl(nextRoom.roomCode);
    renderSurface();
  }

  function handleMessage(message: PartyServerMessage): void {
    if (message.type === 'room' || message.type === 'state') {
      handleRoomUpdate(message.room, message.type === 'room' && message.reconnected);
      return;
    }
    if (message.type === 'error') {
      notice = message.message;
      renderSurface(true);
      return;
    }
    if (message.type === 'closed') {
      room = null;
      notice = closedReasonCopy(message.reason);
      updateUrl(null);
      renderSurface(true);
    }
  }

  function entryValues(): { nickname: string; roomCode: string } {
    return {
      nickname: requireElement<HTMLInputElement>('#relay-nickname').value.trim(),
      roomCode: normalizePartyRoomCode(requireElement<HTMLInputElement>('#relay-room-code').value),
    };
  }

  function inviteUrl(): string {
    return partyInviteUrl(GAME_PATH, room?.roomCode ?? '');
  }

  async function copyInvite(): Promise<void> {
    if (!room) return;
    const copied = await copyPartyText(inviteUrl());
    notice = copied ? '초대 링크를 복사했어요.' : '링크를 복사하지 못했습니다.';
    renderSurface(true);
  }

  async function shareInvite(): Promise<void> {
    if (!room) return;
    const mode = await sharePartyInvite(GAME_TITLE, `${room.roomCode} 방에서 같이 그림릴레이 해요!`, inviteUrl());
    notice = kakaoCardShareMessage(mode);
    renderSurface(true);
  }

  function resultCanvas(view: Extract<DrawingRelayView, { mode: 'complete' }>): HTMLCanvasElement {
    const columns = Math.min(3, view.chains.length);
    const columnWidth = columns === 1 ? 1_080 : 720;
    const gap = 30;
    const entryStep = 135;
    const entryHeight = 115;
    const chainHeights = view.chains.map((chain) => 60 + chain.length * entryStep);
    const rowCount = Math.ceil(view.chains.length / columns);
    const rowHeights = Array.from({ length: rowCount }, (_, row) => Math.max(...chainHeights.slice(row * columns, row * columns + columns)));
    const canvas = document.createElement('canvas');
    canvas.width = columns * columnWidth + (columns + 1) * gap;
    canvas.height = 130 + rowHeights.reduce((sum, height) => sum + height, 0) + (rowCount + 1) * gap;
    const context = canvas.getContext('2d');
    if (!context) return canvas;
    context.fillStyle = '#e8f8f5';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#172338';
    context.font = '900 44px sans-serif';
    context.fillText('파도콩 그림릴레이', gap, 62);
    context.fillStyle = '#607184';
    context.font = '600 20px sans-serif';
    context.fillText('oneultool.com', gap, 98);
    const rowOffsets: number[] = [];
    let runningY = 130 + gap;
    for (const height of rowHeights) {
      rowOffsets.push(runningY);
      runningY += height + gap;
    }
    view.chains.forEach((chain, chainIndex) => {
      const column = chainIndex % columns;
      const row = Math.floor(chainIndex / columns);
      const left = gap + column * (columnWidth + gap);
      let top = rowOffsets[row];
      context.fillStyle = '#172338';
      context.font = '900 24px sans-serif';
      context.fillText(`STORY ${chainIndex + 1}`, left + 18, top + 31);
      top += 48;
      for (const artifact of chain) {
        context.fillStyle = '#ffffff';
        context.strokeStyle = '#172338';
        context.lineWidth = 4;
        context.beginPath();
        context.roundRect(left, top, columnWidth, entryHeight, 16);
        context.fill();
        context.stroke();
        const player = participantForId(artifact.authorId);
        context.fillStyle = '#607184';
        context.font = '700 14px sans-serif';
        context.fillText(`${player?.nickname ?? '파도콩'}${artifact.timedOut ? ' · 시간 초과' : ''}`, left + 16, top + 22);
        if (artifact.kind === 'text') {
          context.fillStyle = '#172338';
          context.font = '900 22px sans-serif';
          const text = Array.from(artifact.text).slice(0, 32).join('');
          context.fillText(text, left + 16, top + 74, columnWidth - 32);
        } else {
          drawStrokes(context, artifact.strokes, left + 16, top + 29, columnWidth - 32, 78);
        }
        top += entryStep;
      }
    });
    return canvas;
  }

  function canvasBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  }

  async function saveOrShareResult(share: boolean): Promise<void> {
    const view = gameView();
    if (!view || view.mode !== 'complete') return;
    notice = '결과 이미지를 만드는 중이에요…';
    renderSurface(true);
    const blob = await canvasBlob(resultCanvas(view));
    if (!blob) {
      notice = '결과 이미지를 만들지 못했습니다.';
      renderSurface(true);
      return;
    }
    const file = new File([blob], 'wavebean-drawing-relay.png', { type: 'image/png' });
    if (share && navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ title: GAME_TITLE, text: '우리 그림릴레이 결과를 봐!', files: [file] });
        notice = '결과 이미지를 공유했어요.';
        renderSurface(true);
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          notice = '';
          renderSurface(true);
          return;
        }
      }
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
    notice = share ? '공유 기능 대신 PNG 파일로 저장했어요.' : 'PNG 파일을 저장했어요.';
    renderSurface(true);
  }

  surface.addEventListener('submit', (event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    if (form.id === 'relay-entry-form') {
      const submitter = (event as SubmitEvent).submitter as HTMLButtonElement | null;
      const values = entryValues();
      if (!values.nickname) {
        notice = '닉네임을 입력해 주세요.';
        renderSurface(true);
        return;
      }
      savePartyNickname(values.nickname);
      notice = '';
      if (submitter?.dataset.entryMode === 'join') {
        if (values.roomCode.length !== 6) {
          notice = '6자리 방 코드를 입력해 주세요.';
          renderSurface(true);
          return;
        }
        network.joinRoom(values.roomCode, values.nickname);
      } else {
        network.createRoom(values.nickname);
      }
      return;
    }
    if (form.id === 'relay-text-form') {
      const view = gameView();
      const input = requireElement<HTMLInputElement>('#relay-task-text');
      if (!view || view.mode !== 'stage' || !input.value.trim()) return;
      const actionType = form.dataset.task === 'prompt' ? 'submit-prompt' : 'submit-guess';
      network.action({ type: actionType, phaseId: view.phaseId, text: input.value });
      input.disabled = true;
      form.querySelector<HTMLButtonElement>('button[type="submit"]')!.disabled = true;
    }
  });

  surface.addEventListener('input', (event) => {
    const input = event.target as HTMLInputElement;
    if (input.id === 'relay-room-code') input.value = normalizePartyRoomCode(input.value);
  });

  surface.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-party-action], [data-color], [data-width]');
    if (!target) return;
    if (target.dataset.color) {
      selectedColor = target.dataset.color as DrawingRelayColor;
      surface.querySelectorAll('[data-color]').forEach((button) => button.classList.toggle('selected', button === target));
      return;
    }
    if (target.dataset.width) {
      selectedWidth = target.dataset.width as DrawingRelayWidth;
      surface.querySelectorAll('[data-width]').forEach((button) => button.classList.toggle('selected', button === target));
      return;
    }
    const action = target.dataset.partyAction;
    const view = gameView();
    if (action === 'copy-code' && room) void copyPartyText(room.roomCode);
    else if (action === 'copy-invite') void copyInvite();
    else if (action === 'share-invite') void shareInvite();
    else if (action === 'ready') network.setReady(!(ownPlayer()?.ready ?? false));
    else if (action === 'start') network.start();
    else if (action === 'leave') {
      if (room?.phase === 'playing' && !window.confirm('진행 중인 그림릴레이에서 나갈까요?')) return;
      network.leave();
      room = null;
      notice = '방에서 나왔습니다.';
      updateUrl(null);
      renderSurface(true);
    } else if (action === 'undo' && view?.mode === 'stage') {
      network.action({ type: 'undo', phaseId: view.phaseId });
    } else if (action === 'clear' && view?.mode === 'stage') {
      if (window.confirm('지금까지 그린 그림을 모두 지울까요?')) network.action({ type: 'clear', phaseId: view.phaseId });
    } else if (action === 'submit-drawing' && view?.mode === 'stage') {
      if (localStrokes.length === 0 && !window.confirm('빈 그림을 그대로 전달할까요?')) return;
      network.action({ type: 'submit-drawing', phaseId: view.phaseId });
      (target as HTMLButtonElement).disabled = true;
    } else if (action === 'next-reveal' && view?.mode === 'reveal') {
      network.action({ type: 'next-reveal', phaseId: view.phaseId });
      (target as HTMLButtonElement).disabled = true;
    } else if (action === 'rematch') network.rematch();
    else if (action === 'save-result') void saveOrShareResult(false);
    else if (action === 'share-result') void saveOrShareResult(true);
    else if (action === 'reload') window.location.reload();
  });

  document.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-party-action="copy-code"]');
    if (target && room) void copyPartyText(room.roomCode);
  });

  window.addEventListener('resize', () => {
    const canvas = document.querySelector<HTMLCanvasElement>('#relay-drawing-canvas');
    if (canvas) paintEditorCanvas(canvas);
  });
  window.addEventListener('beforeunload', () => network.dispose());

  window.setInterval(updateTimer, 250);
  window.setInterval(() => network.ping(), 15_000);
  updateConnection();
  renderSurface(true);
  if (incomingRoom) network.resumeRoom(incomingRoom, savedPartyNickname());
}
